import { throttle } from '@cian/newbuilding-utils';
import { useIntersectionObserver } from '@cian/react-utils';

import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import { PikLogoContainer } from 'shared/components/PikLogo';
import { Callback } from 'shared/containers/Callback';
import { PikPhoneNumber } from 'shared/containers/PikPhoneNumber';
import { ECallbackPlace } from 'shared/types/callback';

import { Header } from './Header';
import { trackHeaderNavSectionClick } from './internal/tracking/trackHeaderNavSectionClick';
import { trackShowHeaderSection } from './internal/tracking/trackShowHeaderSection';
import { getPrevSiblingDistanceToTop } from './internal/utils/getPrevSiblingDistanceToTop';

export const HeaderContainer: FC = () => {
  const [isHeaderFixed, setHeaderFixed] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const headerHeight = headerRef.current ? headerRef.current.getBoundingClientRect().height : 0;
  const prevSiblingDistanceToTop = getPrevSiblingDistanceToTop(headerRef.current);
  const place = isHeaderFixed ? ECallbackPlace.BlockStickyTop : ECallbackPlace.HeadBlock;

  const handleFixHeader = useCallback(() => {
    const header = headerRef.current;

    /* istanbul ignore next */
    if (!header) {
      return;
    }

    const windowYOffset = window.pageYOffset;

    if (windowYOffset > prevSiblingDistanceToTop + headerHeight) {
      setHeaderFixed(true);
    } else {
      setHeaderFixed(false);
    }
  }, [headerHeight, prevSiblingDistanceToTop]);
  const handleScroll = throttle(200, handleFixHeader);

  useEffect(() => {
    handleFixHeader();
    window.addEventListener('scroll', handleScroll);

    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleFixHeader, handleScroll]);

  useIntersectionObserver({
    elementRef: headerRef,
    onInViewport: ({ subscribeState }) => {
      subscribeState.setSubscribeUnavailable();

      trackShowHeaderSection();
    },
  });

  return (
    <Header
      callback={<Callback place={place} size="small" />}
      isFixed={isHeaderFixed}
      logo={<PikLogoContainer />}
      number={<PikPhoneNumber />}
      ref={headerRef}
      onNavSectionClick={trackHeaderNavSectionClick}
    />
  );
};
